import React, { createContext, useContext, useState, useCallback } from 'react';
import demandConsult from '../services/demandConsult';

interface ISourceContext {
  sources: string[];
  selectedApi: string;
  setSources: (sources: string[], api?: string) => void;
  getSource: (sourceId: string) => Promise<any>;
}

export const SourceContext = createContext<ISourceContext>({
  sources: [],
  selectedApi: "all",
  setSources: () => {},
  getSource: async () => {},
});

/**
 * Provider para gerenciar múltiplas fontes.
 * 
 * Mantém uma lista de IDs de fontes para monitoramento e fornece
 * função para buscar dados de uma fonte específica.
 */
const SourceProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [sources, setSourcesState] = useState<string[]>([]);
  const [selectedApi, setSelectedApi] = useState<string>("all");

  /**
   * Define as fontes e a API selecionada.
   */
  const setSources = useCallback((sourcesList: string[], api: string = "all") => {
    setSourcesState(sourcesList);
    setSelectedApi(api);
  }, []);

  /**
   * Busca dados de uma fonte específica.
   * 
   * @param sourceId - ID da fonte a ser buscada
   * @returns Dados da fonte encontrada
   */
  const getSource = useCallback(async (sourceId: string): Promise<any> => {
    try {
      const result = await demandConsult.getDemand({ sourceId, api: selectedApi });
      return result;
    } catch (error: any) {
      console.error('Erro ao buscar fonte:', error);
      throw error;
    }
  }, [selectedApi]);

  return (
    <SourceContext.Provider
      value={{
        sources,
        selectedApi,
        setSources,
        getSource,
      }}
    >
      {children}
    </SourceContext.Provider>
  );
};

/**
 * Hook para acessar o contexto de fontes.
 * 
 * @returns Contexto de fontes
 */
const useSource = (): ISourceContext => {
  const context = useContext(SourceContext);
  if (!context) {
    throw new Error('useSource must be used within a SourceProvider');
  }
  return context;
};

export { SourceProvider, useSource };

